import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Plus, AlertTriangle, Clock, FileWarning, Loader2, RefreshCw, CheckCircle2,
    FileText, ExternalLink, Zap, BellOff, Download, Upload, Undo2, Gavel,
    Search, X, Eye, EyeOff, ShieldCheck, Repeat, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { UnidadeConsumidora } from '../../types';
import {
    listarSolicitacoes, listarAlertas, atualizarStatusSolicitacao, registrarRetornoClienteCancelamento,
    tratarAlertasEmLote, dispensarAlertasEmLote, situacoesDasUcs,
    resumirAlertas, filtrarAlertas, filtrarSolicitacoes, RESOLUCAO_ALERTA_LABEL,
    SolicitacaoCancelamentoDetalhada, CancelamentoAlerta, AlertaResumido,
    MOTIVO_LABEL, STATUS_SOLICITACAO_LABEL, StatusSolicitacaoCancelamento, OrigemSolicitacao,
    TipoAlertaCancelamento,
} from '../../src/lib/cancelamentos';
import {
    estadoPrazoRetorno, diasDeAtraso, hojeEmBrasilia, ORIGEM_SOLICITACAO_LABEL, ORIGENS_SOLICITACAO,
} from '../../src/lib/prazoRetornoCancelamento';
import { resumirEnviosPorSolicitacao, situacaoEnvio } from '../../src/lib/envioCancelamento';
import type { ItemEnvio } from '../../src/lib/envioCancelamento';
import { exportarCancelamentos } from '../../src/lib/cancelamentosExport';
import { listarLotes, desfazerLote, LoteImportado } from '../../src/lib/cancelamentosImportacao';
import NovaSolicitacaoCancelamentoModal from './NovaSolicitacaoCancelamentoModal';
import ImportarCancelamentosModal from './ImportarCancelamentosModal';
import CancelUCModal from './CancelUCModal';
import ValidarCancelamentoModal from './ValidarCancelamentoModal';
import RespostaCancelamentoModal from './RespostaCancelamentoModal';
import ConfirmModal from '../../src/components/ConfirmModal';
import SolicitacaoHistoricoPopover from './SolicitacaoHistoricoPopover';
import SolicitacaoCancelamentoModal from './SolicitacaoCancelamentoModal';
import { MOTIVOS_RETENCAO, faltaNaRetencao, motivoRetencaoPorCodigo, textoDaRetencao } from '../../src/lib/resultadoCancelamento';
import { paginar, paginaDoIndice, precisaReenviarNaLista, dataSolicitacaoExibida, estadoRetornoCliente } from '../../src/lib/cancelamentosLista';
import {
    fmtBRL, fmtData, STATUS_SOLICITACAO_CLS, RETORNO_META, SELO, linkGerenciarUc, multaNaoCobrada,
} from './cancelamentosVisual';

/**
 * Os dois tipos de alerta são dois trabalhos diferentes, com gente diferente:
 * faturas em aberto se analisa caso a caso (negociou, pagou ou cancela como
 * inadimplente); saldo excedente é a retirada temporária do rateio.
 */
const GRUPO_ALERTA_META: Record<TipoAlertaCancelamento, { label: string; dica: string }> = {
    faturas_em_aberto: {
        label: 'Faturas em aberto',
        dica: 'Confira caso a caso se as faturas foram negociadas ou pagas, ou se o cliente segue para o cancelamento como inadimplente.',
    },
    saldo_excedente: {
        label: 'Com saldo',
        dica: 'UC pausada automaticamente por excesso de saldo — avalie a retirada temporária do rateio.',
    },
};

/**
 * Uma linha por CASO de alerta (UC + tipo), não por linha do banco — ver
 * `resumirAlertas`. Fora do componente pai de propósito: definir componente
 * dentro de componente remonta a árvore inteira a cada tecla da busca.
 */
function LinhaAlerta({ a, foco, dispensando, motivoDispensa, onMotivoChange, onIniciarDispensa, onCancelarDispensa, onDispensar, onTratar }: {
    a: AlertaResumido;
    foco: boolean;
    dispensando: boolean;
    motivoDispensa: string;
    onMotivoChange: (v: string) => void;
    onIniciarDispensa: () => void;
    onCancelarDispensa: () => void;
    onDispensar: () => void;
    onTratar: () => void;
}) {
    return (
        <div id={`cancelamento-alerta-${a.id}`}
            className={`rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-slate-800/60 px-4 py-3 text-sm border-l-4 ${a.tipo === 'faturas_em_aberto' ? 'border-l-red-600' : 'border-l-blue-600'} ${foco ? 'ring-2 ring-primary/50' : ''} ${a.resolucao ? 'opacity-70' : ''}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-slate-100">
                        {a.cliente_nome || a.uc_numero} — UC {a.uc_numero}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                        {a.tipo === 'faturas_em_aberto'
                            ? `${a.detalhe.qtd_faturas} faturas em aberto (${fmtBRL(Number(a.detalhe.valor_total || 0))}) — avaliar retirada por inadimplência.`
                            : `Saldo cobre ${a.detalhe.cobertura_meses} meses de consumo — UC pausada automaticamente, avalie retirada temporária.`}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {a.resolucao ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50">
                                <CheckCircle2 size={9} /> {RESOLUCAO_ALERTA_LABEL[a.resolucao]}
                            </span>
                        ) : null}
                        {a.solicitacaoVinculada && !a.resolucao ? (
                            <span title="Já existe solicitação de cancelamento para esta UC"
                                className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/50">
                                <FileText size={9} /> Solicitação #{a.solicitacaoVinculada.id} — {STATUS_SOLICITACAO_LABEL[a.solicitacaoVinculada.status]}
                            </span>
                        ) : null}
                        {a.repeticoes > 1 ? (
                            <span title={`O alerta se repete desde ${fmtData(a.primeiraOcorrencia)} — o robô grava um por dia enquanto o caso não é tratado. Tratar ou dispensar fecha todos de uma vez.`}
                                className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700">
                                <Repeat size={9} /> {a.repeticoes}× desde {fmtData(a.primeiraOcorrencia)}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {dispensando ? (
                        <div className="flex items-center gap-1.5">
                            <input value={motivoDispensa} onChange={e => onMotivoChange(e.target.value)} placeholder="Motivo"
                                className="text-xs px-2 py-1.5 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-slate-100" />
                            <button onClick={onDispensar} className="px-2 py-1.5 rounded-md bg-gray-600 text-white text-xs font-bold">Confirmar</button>
                            <button onClick={onCancelarDispensa} className="px-2 py-1.5 rounded-md text-xs text-gray-500 dark:text-slate-400">Voltar</button>
                        </div>
                    ) : (
                        <>
                            <button onClick={onTratar} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-100 transition-colors">
                                <CheckCircle2 size={12} /> Tratado
                            </button>
                            <button onClick={onIniciarDispensa} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 text-xs font-bold hover:bg-gray-50 transition-colors">
                                <BellOff size={12} /> Dispensar
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Com `onClick` o cartão vira botão (#6120): os dois de alerta abrem o bloco de
 * Alertas Automáticos já no grupo. Os outros três seguem só informativos — o
 * contorno e a seta de "Ver casos" é o que separa um do outro à primeira vista.
 */
function KpiCard({ icon: Icon, tint, titulo, valor, subtexto, onClick, ativo }: {
    icon: React.ElementType; tint: string; titulo: string; valor: string; subtexto?: string;
    onClick?: () => void; ativo?: boolean;
}) {
    const conteudo = (
        <>
            <div className={`p-2.5 rounded-xl shrink-0 ${tint}`}><Icon size={18} /></div>
            <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{titulo}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 truncate">{valor}</p>
                {subtexto && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{subtexto}</p>}
                {onClick ? (
                    <p className="text-[11px] font-bold text-primary mt-1 inline-flex items-center gap-0.5">Ver casos <ChevronRight size={11} /></p>
                ) : null}
            </div>
        </>
    );
    const base = 'bg-white dark:bg-gray-800 rounded-2xl border shadow-sm p-4 flex items-start gap-3';
    if (!onClick) return <div className={`${base} border-gray-100 dark:border-gray-700`}>{conteudo}</div>;
    return (
        <button type="button" onClick={onClick} aria-pressed={ativo} title="Abre os alertas automáticos deste grupo"
            className={`${base} text-left transition-all cursor-pointer hover:shadow-md hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${ativo
                ? 'border-primary/50 ring-1 ring-primary/30'
                : 'border-gray-100 dark:border-gray-700'}`}>
            {conteudo}
        </button>
    );
}

const CancelamentosView: React.FC = () => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    // Os crons de alerta automático (popular_alerta_faturas_em_aberto /
    // popular_alerta_saldo_excedente) linkam a notificação para cá com
    // `?alerta=<id>` — sem isso, quem clica na notificação cai numa lista
    // genérica e precisa procurar o alerta manualmente.
    const alertaFocoId = searchParams.get('alerta');
    const [loading, setLoading] = useState(true);
    const [solicitacoes, setSolicitacoes] = useState<SolicitacaoCancelamentoDetalhada[]>([]);
    const [alertas, setAlertas] = useState<CancelamentoAlerta[]>([]);
    const [situacaoPorUc, setSituacaoPorUc] = useState<Map<number, string | null>>(new Map());
    const [filtroStatus, setFiltroStatus] = useState<StatusSolicitacaoCancelamento | 'todas'>('todas');
    const [filtroOrigem, setFiltroOrigem] = useState<OrigemSolicitacao | 'todas'>('todas');
    const [busca, setBusca] = useState('');
    // Filtrar 100+ linhas a cada tecla trava a digitação; o valor adiado deixa o
    // campo responder na hora e a lista alcançar logo em seguida.
    const buscaAdiada = useDeferredValue(busca);
    // Busca própria do bloco de alertas (#6120). Antes o mesmo campo filtrava a
    // lista e os alertas: quem procurava um cliente na lista via os alertas
    // sumirem sem entender por quê.
    const [buscaAlertas, setBuscaAlertas] = useState('');
    const buscaAlertasAdiada = useDeferredValue(buscaAlertas);
    const [mostrarResolvidos, setMostrarResolvidos] = useState(false);
    const [grupoAlerta, setGrupoAlerta] = useState<TipoAlertaCancelamento | 'todos'>('todos');
    const grupoAjustadoParaFoco = useRef<string | null>(null);
    // Bloco de alertas recolhido por padrão (#6120): aberto, ele empurrava a lista
    // de solicitações para baixo. Abre pelos cartões de alerta, pelo botão da
    // faixa, ou sozinho quando a pessoa chega pela notificação (`?alerta=`).
    const [alertasAbertos, setAlertasAbertos] = useState(() => !!alertaFocoId);
    const [destacarAlertas, setDestacarAlertas] = useState(false);
    const blocoAlertasRef = useRef<HTMLDivElement>(null);
    const [showNovaSolicitacao, setShowNovaSolicitacao] = useState(false);
    const [validandoSolicitacao, setValidandoSolicitacao] = useState<SolicitacaoCancelamentoDetalhada | null>(null);
    const [respondendoSolicitacao, setRespondendoSolicitacao] = useState<SolicitacaoCancelamentoDetalhada | null>(null);
    const [distratoParaUc, setDistratoParaUc] = useState<{ uc: UnidadeConsumidora; solicitacaoId: number } | null>(null);
    const [dispensandoId, setDispensandoId] = useState<string | null>(null);
    const [motivoDispensa, setMotivoDispensa] = useState('');
    const [confirmAcao, setConfirmAcao] = useState<{ s: SolicitacaoCancelamentoDetalhada; status: StatusSolicitacaoCancelamento } | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [motivoConclusao, setMotivoConclusao] = useState('');
    // Por que o cliente ficou, em lista fechada (#6509): é o que mede a retenção.
    const [codigoRetencao, setCodigoRetencao] = useState('');
    const [historicoAberto, setHistoricoAberto] = useState<{ solicitacaoId: number; anchor: DOMRect } | null>(null);
    const [showImportar, setShowImportar] = useState(false);
    const [lotes, setLotes] = useState<LoteImportado[]>([]);
    const [confirmDesfazer, setConfirmDesfazer] = useState<LoteImportado | null>(null);
    const [confirmLimparResolvidos, setConfirmLimparResolvidos] = useState(false);
    // Envios por solicitação (#5830): sem isto a tela diz "Resposta enviada"
    // mesmo quando o WhatsApp falhou, e o atendente acha que o cliente soube.
    const [enviosPorSolicitacao, setEnviosPorSolicitacao] = useState<Map<number, ItemEnvio[]>>(new Map());
    // Modal da solicitação (#6121). Guarda o id, não o objeto: depois de uma ação
    // o `carregar()` troca a lista e o modal passa a mostrar a versão nova.
    const [solicitacaoAbertaId, setSolicitacaoAbertaId] = useState<number | null>(null);
    const [pagina, setPagina] = useState(1);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [sols, als, lts] = await Promise.all([listarSolicitacoes(), listarAlertas(), listarLotes()]);
            // A situação atual das UCs alertadas é o que diz se o caso já foi
            // resolvido — sem isso o alerta de uma UC cancelada fica pendente
            // para sempre (eram 91 linhas assim em 09/09/2026).
            const situacoes = await situacoesDasUcs(als.map(a => a.uc_id));
            // Falha aqui não pode derrubar a tela inteira: sem os envios a lista
            // ainda abre, só volta a mostrar o rótulo antigo.
            const envios = await resumirEnviosPorSolicitacao(sols.map(s => s.id))
                .catch(e => { console.error('Erro ao carregar envios das solicitações', e); return new Map<number, ItemEnvio[]>(); });
            setEnviosPorSolicitacao(envios);
            setSolicitacoes(sols);
            setAlertas(als);
            setSituacaoPorUc(situacoes);
            setLotes(lts);
        } catch (error: any) {
            console.error('Erro ao carregar Cancelamentos', error);
            addToast('Erro ao carregar dados de cancelamento: ' + (error.message || ''), 'error');
        } finally {
            setLoading(false);
        }
    }, [addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    // Casos (UC + tipo), não linhas do banco: o cron grava uma linha por dia
    // enquanto o caso não é tratado. Ver `resumirAlertas`.
    const casos = useMemo(
        () => resumirAlertas(alertas, solicitacoes, situacaoPorUc),
        [alertas, solicitacoes, situacaoPorUc]);
    const casosResolvidos = useMemo(() => casos.filter(c => c.resolucao !== null), [casos]);
    const casosAbertos = useMemo(() => casos.filter(c => c.resolucao === null), [casos]);
    const casosDaLista = mostrarResolvidos ? casos : casosAbertos;
    const qtdPorGrupo = useMemo(() => ({
        todos: casosDaLista.length,
        faturas_em_aberto: casosDaLista.filter(c => c.tipo === 'faturas_em_aberto').length,
        saldo_excedente: casosDaLista.filter(c => c.tipo === 'saldo_excedente').length,
    }), [casosDaLista]);
    const casosVisiveis = useMemo(
        () => filtrarAlertas(grupoAlerta === 'todos' ? casosDaLista : casosDaLista.filter(c => c.tipo === grupoAlerta), buscaAlertasAdiada),
        [casosDaLista, grupoAlerta, buscaAlertasAdiada]);

    /** Cartão de alerta clicado: abre o bloco no grupo, rola até ele e pisca, para o clique não parecer que não fez nada. */
    const abrirAlertas = (grupo: TipoAlertaCancelamento | 'todos') => {
        setGrupoAlerta(grupo);
        setAlertasAbertos(true);
        setDestacarAlertas(true);
        requestAnimationFrame(() => blocoAlertasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };
    useEffect(() => {
        if (!destacarAlertas) return;
        const t = setTimeout(() => setDestacarAlertas(false), 1600);
        return () => clearTimeout(t);
    }, [destacarAlertas]);

    /**
     * A notificação aponta para UMA linha (`?alerta=<id>`), que pode ser uma
     * repetição antiga — e a tela renderiza só a ocorrência mais nova do caso.
     * Sem traduzir o id para o caso, quem clica na notificação não acha nada; e
     * se o caso já estiver resolvido, ele nem está sendo desenhado.
     */
    const casoEmFoco = useMemo(
        () => (alertaFocoId ? casos.find(c => c.idsDoCaso.includes(alertaFocoId)) ?? null : null),
        [alertaFocoId, casos]);

    // Chegou pela notificação: deixa o caso visível — bloco aberto, busca dos
    // alertas limpa, resolvidos à mostra se for o caso, grupo certo. UMA vez por
    // caso: o `?alerta=` fica na URL, e reaplicar isso a cada render impediria
    // o analista de recolher o bloco ou trocar de grupo depois.
    const [rolarParaFoco, setRolarParaFoco] = useState(false);
    useEffect(() => {
        if (!casoEmFoco || grupoAjustadoParaFoco.current === casoEmFoco.id) return;
        grupoAjustadoParaFoco.current = casoEmFoco.id;
        setAlertasAbertos(true);
        setBuscaAlertas('');
        if (casoEmFoco.resolucao) setMostrarResolvidos(true);
        setGrupoAlerta(g => (g !== 'todos' && g !== casoEmFoco.tipo ? casoEmFoco.tipo : g));
        setRolarParaFoco(true);
    }, [casoEmFoco]);
    // Rola quando a linha já existe na tela (depende dos ajustes acima terem renderizado).
    useEffect(() => {
        if (!rolarParaFoco || !casoEmFoco) return;
        const el = document.getElementById(`cancelamento-alerta-${casoEmFoco.id}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setRolarParaFoco(false);
    }, [rolarParaFoco, casoEmFoco, casosVisiveis]);

    const kpis = useMemo(() => {
        const abertas = solicitacoes.filter(s => s.status === 'aberta' || s.status === 'em_andamento').length;
        const hoje = hojeEmBrasilia(new Date());
        const retornoVencido = solicitacoes.filter(s => estadoPrazoRetorno(
            { dataRetornoPrevista: s.dataRetornoPrevista, status: s.status, validadoEm: s.validadoEm }, hoje) === 'vencida').length;
        const faturasRisco = casosAbertos.filter(a => a.tipo === 'faturas_em_aberto').length;
        const saldoExcedente = casosAbertos.filter(a => a.tipo === 'saldo_excedente').length;
        // Importados ficam de fora do tempo médio: `concluida_em` deles é a data
        // que veio da planilha, não o instante em que o processo terminou de
        // fato — misturar os dois faz a média medir coisa nenhuma.
        const concluidas = solicitacoes.filter(s => s.status === 'concluida' && s.concluida_em && s.origem !== 'importacao');
        const tempoMedioDias = concluidas.length > 0
            ? concluidas.reduce((acc, s) => acc + (new Date(s.concluida_em!).getTime() - new Date(s.data_solicitacao).getTime()) / 86400000, 0) / concluidas.length
            : null;
        // Dado que só passou a existir com a importação: quantos cancelamentos
        // saíram sem cobrar a multa contratual.
        const comMulta = solicitacoes.filter(s => s.multaValor !== null && s.multaValor > 0).length;
        const multaNaoCobrada = solicitacoes.filter(s => !!s.multaSituacao && /(NAO|NÃO)\s*(FOI\s*)?COBR/i.test(s.multaSituacao)).length;
        return { abertas, retornoVencido, faturasRisco, saldoExcedente, tempoMedioDias, comMulta, multaNaoCobrada };
    }, [solicitacoes, casosAbertos]);

    const solicitacoesFiltradas = useMemo(() => {
        const porStatus = filtroStatus === 'todas' ? solicitacoes : solicitacoes.filter(s => s.status === filtroStatus);
        const porOrigem = filtroOrigem === 'todas' ? porStatus : porStatus.filter(s => s.origem === filtroOrigem);
        return filtrarSolicitacoes(porOrigem, buscaAdiada);
    }, [solicitacoes, filtroStatus, filtroOrigem, buscaAdiada]);

    // Trocar filtro ou busca volta para a página 1 — senão dá para ficar numa
    // página 4 que o novo filtro nem tem.
    useEffect(() => { setPagina(1); }, [filtroStatus, filtroOrigem, buscaAdiada]);

    // Só a página da tela. O Exportar continua recebendo `solicitacoesFiltradas`
    // inteira — ver `paginar`.
    const paginaAtual = useMemo(() => paginar(solicitacoesFiltradas, pagina), [solicitacoesFiltradas, pagina]);

    // Quem chega pela notificação de um alerta cuja UC já tem solicitação: a lista
    // pula para a página dela, ou a pessoa cai na tela e não acha o caso (#6121).
    const paginaAjustadaParaFoco = useRef<string | null>(null);
    useEffect(() => {
        const vinculada = casoEmFoco?.solicitacaoVinculada;
        if (!casoEmFoco || !vinculada || paginaAjustadaParaFoco.current === casoEmFoco.id) return;
        const indice = solicitacoesFiltradas.findIndex(s => s.id === vinculada.id);
        if (indice < 0) return;
        paginaAjustadaParaFoco.current = casoEmFoco.id;
        setPagina(paginaDoIndice(indice));
    }, [casoEmFoco, solicitacoesFiltradas]);

    const solicitacaoAberta = useMemo(
        () => (solicitacaoAbertaId === null ? null : solicitacoes.find(s => s.id === solicitacaoAbertaId) ?? null),
        [solicitacaoAbertaId, solicitacoes]);
    // Um modal de ação por cima do da solicitação: o Esc é dele.
    const modalDeAcaoAberto = !!validandoSolicitacao || !!respondendoSolicitacao || !!distratoParaUc
        || !!confirmAcao || !!historicoAberto;

    const hojeBrasilia = hojeEmBrasilia(new Date());

    const copiarProtocolo = async (protocolo: string) => {
        try {
            await navigator.clipboard.writeText(protocolo);
            addToast(`Protocolo ${protocolo} copiado.`, 'success');
        } catch {
            addToast('Não foi possível copiar. Selecione o protocolo e copie manualmente.', 'warning');
        }
    };

    const handleTratarAlerta = async (alerta: AlertaResumido) => {
        try {
            // Fecha TODAS as repetições do caso: marcar só a linha do dia deixaria
            // as outras pendentes e o caso voltaria para a tela amanhã.
            const qtd = await tratarAlertasEmLote(alerta.idsDoCaso, user?.id);
            addToast(qtd > 1 ? `Caso tratado (${qtd} alertas repetidos fechados).` : 'Alerta marcado como tratado.', 'success');
            carregar();
        } catch (error: any) {
            addToast('Erro ao tratar alerta: ' + (error.message || ''), 'error');
        }
    };

    const handleDispensarAlerta = async (alerta: AlertaResumido) => {
        if (!motivoDispensa.trim()) {
            addToast('Informe o motivo da dispensa.', 'warning');
            return;
        }
        try {
            const qtd = await dispensarAlertasEmLote(alerta.idsDoCaso, motivoDispensa.trim(), user?.id);
            addToast(qtd > 1 ? `Caso dispensado (${qtd} alertas repetidos fechados).` : 'Alerta dispensado.', 'success');
            setDispensandoId(null);
            setMotivoDispensa('');
            carregar();
        } catch (error: any) {
            addToast('Erro ao dispensar alerta: ' + (error.message || ''), 'error');
        }
    };

    /** Fecha de uma vez os casos que já foram resolvidos fora da tela. */
    const handleLimparResolvidos = async () => {
        const ids = casosResolvidos.flatMap(c => c.idsDoCaso);
        if (ids.length === 0) return;
        setConfirmLoading(true);
        try {
            const qtd = await tratarAlertasEmLote(ids, user?.id);
            addToast(`${casosResolvidos.length} caso(s) já resolvido(s) fechado(s) — ${qtd} alerta(s) no total.`, 'success');
            setConfirmLimparResolvidos(false);
            carregar();
        } catch (error: any) {
            addToast('Erro ao limpar os alertas resolvidos: ' + (error.message || ''), 'error');
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleAtualizarStatus = async (s: SolicitacaoCancelamentoDetalhada, status: StatusSolicitacaoCancelamento, motivo: string) => {
        try {
            await atualizarStatusSolicitacao(s.id, status, motivo);
            addToast(`Solicitação marcada como "${STATUS_SOLICITACAO_LABEL[status]}".`, 'success');
            carregar();
        } catch (error: any) {
            addToast('Erro ao atualizar solicitação: ' + (error.message || ''), 'error');
        }
    };

    const handleConfirmarAcao = async () => {
        if (!confirmAcao) return;
        // Desistência sempre exige motivo (mesmo padrão da dispensa de alerta);
        // conclusão aceita o campo em branco (nem todo motivo de cancelamento
        // exige justificar o encerramento, ex.: distrato já cobre o porquê).
        const faltaRetencao = confirmAcao.status === 'cancelada' ? faltaNaRetencao(codigoRetencao, motivoConclusao) : null;
        if (faltaRetencao) {
            addToast(faltaRetencao, 'warning');
            return;
        }
        setConfirmLoading(true);
        await handleAtualizarStatus(confirmAcao.s, confirmAcao.status,
            confirmAcao.status === 'cancelada' ? textoDaRetencao(codigoRetencao, motivoConclusao) : motivoConclusao);
        setConfirmLoading(false);
        setConfirmAcao(null);
        setMotivoConclusao(''); setCodigoRetencao('');
    };

    const handleDesfazerLote = async () => {
        if (!confirmDesfazer) return;
        setConfirmLoading(true);
        try {
            const apagadas = await desfazerLote(confirmDesfazer.loteId);
            addToast(`${apagadas} registro(s) importado(s) removido(s).`, 'success');
            setConfirmDesfazer(null);
            carregar();
        } catch (error: any) {
            addToast('Erro ao desfazer a importação: ' + (error.message || ''), 'error');
        } finally {
            setConfirmLoading(false);
        }
    };

    const abrirDistrato = async (s: SolicitacaoCancelamentoDetalhada) => {
        try {
            const { data, error } = await supabase.from('unidades_consumidoras').select('*').eq('id', s.uc_id).single();
            if (error) throw error;
            setDistratoParaUc({ uc: data as UnidadeConsumidora, solicitacaoId: s.id });
        } catch (error: any) {
            addToast('Erro ao carregar dados da UC: ' + (error.message || ''), 'error');
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white font-poppins">Cancelamentos</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Acompanhamento das solicitações de cancelamento, do pedido à conclusão.</p>
                </div>
                <div className="flex items-center flex-wrap gap-2">
                    <button onClick={carregar} className="p-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors" title="Atualizar">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button onClick={() => {
                        // A lista INTEIRA do filtro, nunca `paginaAtual.itens` — a tela
                        // mostra 50 por vez, a planilha não tem por que vir cortada.
                        exportarCancelamentos(solicitacoesFiltradas);
                        const filtroTxt = filtroStatus === 'todas' ? '' : ` (filtro: ${STATUS_SOLICITACAO_LABEL[filtroStatus]})`;
                        addToast(`${solicitacoesFiltradas.length} registro(s) exportado(s)${filtroTxt}.`, 'success');
                    }}
                        disabled={solicitacoesFiltradas.length === 0}
                        title={filtroStatus === 'todas'
                            ? `Exporta as ${solicitacoesFiltradas.length} solicitações do filtro — todas as páginas, não só as 50 da tela. O arquivo gerado é o mesmo modelo aceito na importação.`
                            : `Exporta as ${solicitacoesFiltradas.length} solicitações do filtro atual (${STATUS_SOLICITACAO_LABEL[filtroStatus]}) — todas as páginas, não só as 50 da tela. Para exportar tudo, selecione "Todas" antes.`}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50">
                        <Download size={16} />
                        <span className="hidden sm:inline">
                            Exportar <span className="text-gray-400 dark:text-slate-500 font-normal tabular-nums">({solicitacoesFiltradas.length}{filtroStatus !== 'todas' ? ` · ${STATUS_SOLICITACAO_LABEL[filtroStatus]}` : ''})</span>
                        </span>
                    </button>
                    <button onClick={() => setShowImportar(true)}
                        title="Traz para a aba o histórico que está em planilha"
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm">
                        <Upload size={16} /> <span className="hidden sm:inline">Importar</span>
                    </button>
                    <button onClick={() => setShowNovaSolicitacao(true)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 transition-colors shadow-sm">
                        <Plus size={16} /> Nova Solicitação
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <KpiCard icon={FileWarning} tint="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400" titulo="Solicitações em Aberto" valor={String(kpis.abertas)} subtexto={kpis.retornoVencido > 0 ? `${kpis.retornoVencido} com retorno vencido` : undefined} />
                <KpiCard icon={AlertTriangle} tint="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400" titulo="Risco — Faturas em Aberto" valor={String(kpis.faturasRisco)} subtexto="3+ faturas em aberto na mesma UC"
                    onClick={() => abrirAlertas('faturas_em_aberto')} ativo={alertasAbertos && grupoAlerta === 'faturas_em_aberto'} />
                <KpiCard icon={Zap} tint="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" titulo="Saldo Excedente" valor={String(kpis.saldoExcedente)} subtexto="Recomendação de retirada temporária"
                    onClick={() => abrirAlertas('saldo_excedente')} ativo={alertasAbertos && grupoAlerta === 'saldo_excedente'} />
                <KpiCard icon={Clock} tint="bg-primary/10 text-primary" titulo="Tempo Médio de Conclusão" valor={kpis.tempoMedioDias === null ? '—' : `${kpis.tempoMedioDias.toFixed(0)} dias`} subtexto="Não conta registros importados" />
                <KpiCard icon={Gavel} tint="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400" titulo="Multa Não Cobrada" valor={String(kpis.multaNaoCobrada)} subtexto={`${kpis.comMulta} com multa registrada`} />
            </div>

            {casos.length > 0 ? (
                <div ref={blocoAlertasRef}
                    className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 scroll-mt-4 transition-shadow duration-500 ${destacarAlertas ? 'ring-2 ring-primary/50' : ''}`}>
                    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${alertasAbertos ? 'mb-3' : ''}`}>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <AlertTriangle size={15} className="text-amber-500" />
                            Alertas Automáticos ({casosAbertos.length} a tratar)
                            {alertas.length > casos.length ? (
                                <span title={`O robô grava um alerta por dia enquanto o caso não é tratado: ${alertas.length} linhas para ${casos.length} casos reais. A tela agrupa por cliente + tipo de alerta.`}
                                    className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 normal-case">
                                    {alertas.length} repetições agrupadas
                                </span>
                            ) : null}
                        </h3>
                        <div className="flex items-center flex-wrap gap-2">
                            {!alertasAbertos && casosAbertos.length > 0 ? (
                                <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">
                                    {casosAbertos.filter(c => c.tipo === 'faturas_em_aberto').length} com faturas em aberto · {casosAbertos.filter(c => c.tipo === 'saldo_excedente').length} com saldo
                                </span>
                            ) : null}
                            {alertasAbertos && casosResolvidos.length > 0 ? (
                                <>
                                    <button onClick={() => setMostrarResolvidos(v => !v)}
                                        title="Casos de clientes que já foram cancelados ou cuja solicitação já foi concluída"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                        {mostrarResolvidos ? <EyeOff size={12} /> : <Eye size={12} />}
                                        {mostrarResolvidos ? 'Ocultar' : 'Mostrar'} {casosResolvidos.length} já resolvido(s)
                                    </button>
                                    <button onClick={() => setConfirmLimparResolvidos(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-100 transition-colors">
                                        <CheckCircle2 size={12} /> Limpar resolvidos
                                    </button>
                                </>
                            ) : null}
                            <button onClick={() => (alertasAbertos ? setAlertasAbertos(false) : abrirAlertas(grupoAlerta))}
                                aria-expanded={alertasAbertos} aria-controls="bloco-alertas-cancelamento"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/20 transition-colors">
                                {alertasAbertos ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                {alertasAbertos ? 'Recolher' : 'Abrir alertas'}
                            </button>
                        </div>
                    </div>

                    {alertasAbertos ? (
                    <div id="bloco-alertas-cancelamento">
                    <div className="relative mb-3 max-w-sm">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 pointer-events-none" />
                        <input
                            value={buscaAlertas}
                            onChange={e => setBuscaAlertas(e.target.value)}
                            placeholder="Buscar nos alertas por cliente ou nº da UC..."
                            aria-label="Buscar nos alertas"
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg pl-8 pr-8 py-1.5 text-xs bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none"
                        />
                        {buscaAlertas ? (
                            <button onClick={() => setBuscaAlertas('')} aria-label="Limpar busca dos alertas"
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
                                <X size={12} />
                            </button>
                        ) : null}
                    </div>

                    {casosResolvidos.length > 0 && !mostrarResolvidos ? (
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">
                            {casosResolvidos.length} caso(s) escondido(s) porque o cliente já foi cancelado ou a solicitação já foi concluída.
                        </p>
                    ) : null}

                    <div className="flex items-center gap-2 flex-wrap mb-3" role="group" aria-label="Filtrar alertas por grupo">
                        {(['todos', 'faturas_em_aberto', 'saldo_excedente'] as const).map(g => (
                            <button key={g} onClick={() => setGrupoAlerta(g)} aria-pressed={grupoAlerta === g}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${grupoAlerta === g
                                    ? 'bg-primary/10 border-primary/30 text-primary'
                                    : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                                {g !== 'todos' ? (
                                    <span className={`w-2 h-2 rounded-full ${g === 'faturas_em_aberto' ? 'bg-red-600' : 'bg-blue-600'}`} aria-hidden="true" />
                                ) : null}
                                {g === 'todos' ? 'Todos' : GRUPO_ALERTA_META[g].label}
                                <span className="tabular-nums opacity-70">({qtdPorGrupo[g]})</span>
                            </button>
                        ))}
                    </div>
                    {grupoAlerta !== 'todos' ? (
                        <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-3">{GRUPO_ALERTA_META[grupoAlerta].dica}</p>
                    ) : null}

                    <div className="flex flex-col gap-2">
                        {casosVisiveis.length === 0 ? (
                            <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-6">
                                {buscaAlertas.trim()
                                    ? `Nenhum alerta encontrado para "${buscaAlertas.trim()}".`
                                    : grupoAlerta !== 'todos'
                                        ? `Nenhum alerta de "${GRUPO_ALERTA_META[grupoAlerta].label}" pendente de tratamento. 🎉`
                                        : 'Nenhum alerta pendente de tratamento. 🎉'}
                            </p>
                        ) : casosVisiveis.map(a => (
                            <LinhaAlerta
                                key={`${a.uc_id}-${a.tipo}`}
                                a={a}
                                foco={casoEmFoco?.id === a.id}
                                dispensando={dispensandoId === a.id}
                                motivoDispensa={motivoDispensa}
                                onMotivoChange={setMotivoDispensa}
                                onIniciarDispensa={() => setDispensandoId(a.id)}
                                onCancelarDispensa={() => { setDispensandoId(null); setMotivoDispensa(''); }}
                                onDispensar={() => handleDispensarAlerta(a)}
                                onTratar={() => handleTratarAlerta(a)}
                            />
                        ))}
                    </div>
                    </div>
                    ) : null}
                </div>
            ) : null}

            {lotes.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                        <Upload size={15} className="text-gray-400 dark:text-slate-500" /> Importações realizadas ({lotes.length})
                    </h3>
                    <div className="flex flex-col gap-2">
                        {lotes.map(l => (
                            <div key={l.loteId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-slate-800/60 px-4 py-2.5">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate">{l.arquivo || 'Planilha importada'}</p>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                        {l.quantidade} registro(s) · {new Date(l.criadoEm).toLocaleString('pt-BR')}
                                    </p>
                                </div>
                                <button onClick={() => setConfirmDesfazer(l)}
                                    className="inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 text-xs font-bold hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors">
                                    <Undo2 size={12} /> Desfazer importação
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative flex-1 min-w-0 lg:max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 pointer-events-none" />
                    <input
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        placeholder="Buscar por cliente, nº da UC, CPF/CNPJ, protocolo, vendedor ou distrato..."
                        aria-label="Buscar cancelamentos"
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl pl-9 pr-9 py-2.5 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none"
                    />
                    {busca ? (
                        <button onClick={() => setBusca('')} aria-label="Limpar busca"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                            <X size={14} />
                        </button>
                    ) : null}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {(['todas', 'aberta', 'em_andamento', 'concluida', 'cancelada'] as const).map(s => (
                    <button key={s} onClick={() => setFiltroStatus(s)} aria-pressed={filtroStatus === s}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filtroStatus === s
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        {s === 'todas' ? 'Todas' : STATUS_SOLICITACAO_LABEL[s]}
                    </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap lg:border-l lg:border-gray-200 dark:lg:border-slate-700 lg:pl-3" role="group" aria-label="Filtrar por origem">
                    <span className="text-[11px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Origem</span>
                    {(['todas', ...ORIGENS_SOLICITACAO] as const).map(o => (
                    <button key={o} onClick={() => setFiltroOrigem(o)} aria-pressed={filtroOrigem === o}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filtroOrigem === o
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        {o === 'todas' ? 'Todas' : ORIGEM_SOLICITACAO_LABEL[o]}
                    </button>
                    ))}
                </div>
                {busca.trim() || filtroOrigem !== 'todas' ? (
                    <p className="text-xs text-gray-500 dark:text-slate-400 lg:ml-auto shrink-0">
                        {solicitacoesFiltradas.length} de {solicitacoes.length} solicitação(ões)
                    </p>
                ) : null}
            </div>

            {/* Lista enxuta (#6121). Eram 13 colunas: para ver distrato, prazos e status
                a pessoa rolava para o lado e perdia o nome do cliente, e a barra de
                rolagem só existia no fim da lista. O resto dos dados e as ações moram
                no modal da solicitação — clique (ou Enter) na linha. */}
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
                <div className="max-h-[70vh] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
                            <tr>
                                {['UC / Cliente', 'Data', 'Motivo', 'Faturas em Aberto', 'Status', 'Ações'].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 dark:text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Carregando...</td></tr>
                            ) : solicitacoesFiltradas.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 dark:text-slate-500">
                                    {filtroOrigem !== 'todas' && !busca.trim()
                                        ? `Nenhuma solicitação com origem "${ORIGEM_SOLICITACAO_LABEL[filtroOrigem]}"${filtroStatus !== 'todas' ? ` e status "${STATUS_SOLICITACAO_LABEL[filtroStatus]}"` : ''} ainda.`
                                        : 'Nenhuma solicitação de cancelamento encontrada.'}
                                </td></tr>
                            ) : paginaAtual.itens.map(s => {
                                const estadoRetorno = estadoPrazoRetorno({ dataRetornoPrevista: s.dataRetornoPrevista, status: s.status, validadoEm: s.validadoEm }, hojeBrasilia);
                                const retornoMeta = RETORNO_META[estadoRetorno];
                                const atraso = estadoRetorno === 'vencida' ? diasDeAtraso(s.dataRetornoPrevista, hojeBrasilia) : 0;
                                const reenviar = precisaReenviarNaLista(s, situacaoEnvio(enviosPorSolicitacao.get(s.id)));
                                const aberta = solicitacaoAbertaId === s.id;
                                const dExib = dataSolicitacaoExibida(s, hojeBrasilia);
                                // Selos: só o que pede atenção, no máximo três, nesta ordem
                                // de prioridade. O resto fica no modal.
                                const selos: React.ReactNode[] = [];
                                if (reenviar) selos.push(
                                    <span key="reenviar" title="A resposta ao cliente falhou e não foi reenviada — o cliente não foi avisado."
                                        className={`${SELO} bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50`}>
                                        <AlertTriangle size={9} /> Reenviar resposta
                                    </span>);
                                if (s.situacaoDivergente) selos.push(
                                    <span key="divergente" title="A planilha trouxe como cancelada, mas a UC continua ativa no cadastro — conferir situação real."
                                        className={`${SELO} bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50`}>
                                        <AlertTriangle size={9} /> Divergente
                                    </span>);
                                if (multaNaoCobrada(s.multaSituacao)) selos.push(
                                    <span key="multa" title={s.multaSituacao || undefined}
                                        className={`${SELO} bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/50`}>
                                        <Gavel size={9} /> Multa não cobrada
                                    </span>);
                                if (s.origem === 'portal' || s.origem === 'whatsapp') selos.push(
                                    <span key="origem" className={`${SELO} bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/50`}>
                                        {ORIGEM_SOLICITACAO_LABEL[s.origem]}
                                    </span>);
                                if (s.origem === 'importacao') selos.push(
                                    <span key="importado" title={`Importado da planilha${s.importOrigemPlanilha ? ` — ${s.importOrigemPlanilha}` : ''}`}
                                        className={`${SELO} bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700`}>
                                        Importado
                                    </span>);
                                return (
                                    <tr key={s.id}
                                        tabIndex={0}
                                        aria-label={`Abrir solicitação de ${s.cliente_nome}, UC ${s.uc_numero}`}
                                        onClick={() => setSolicitacaoAbertaId(s.id)}
                                        onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSolicitacaoAbertaId(s.id); } }}
                                        className={`cursor-pointer transition-colors align-top outline-none focus-visible:bg-primary/5 hover:bg-gray-50 dark:hover:bg-slate-800/50 ${aberta ? 'bg-primary/5 dark:bg-primary/10' : ''}`}>
                                        <td className="px-4 py-2.5 min-w-[260px]">
                                            <p className="font-semibold text-gray-800 dark:text-slate-100 leading-snug">{s.cliente_nome}</p>
                                            <p className="text-xs text-gray-400 dark:text-slate-500">UC {s.uc_numero}</p>
                                            {selos.length ? <div className="flex flex-wrap items-center gap-1 mt-1">{selos.slice(0, 3)}</div> : null}
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-600 dark:text-slate-300">
                                            {fmtData(dExib.iso)}
                                            {dExib.ajuste ? (
                                                <p className="mt-1">
                                                    <span title={`Gravada como ${fmtData(dExib.original)}. ${dExib.ajuste === 'dia_mes_trocados' ? 'Dia e mês estavam trocados' : 'O ano estava errado'} na planilha — ajustada pelo mês da aba de onde o registro veio. O banco e a exportação continuam com a data original.`}
                                                        className={`${SELO} bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700 cursor-help`}>
                                                        ajustada pela planilha
                                                    </span>
                                                </p>
                                            ) : null}
                                            {dExib.conferir ? (
                                                <p className="mt-1">
                                                    <span title="Data no futuro que o mês da aba da planilha não explica — conferir na planilha original."
                                                        className={`${SELO} bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50 cursor-help`}>
                                                        <AlertTriangle size={9} /> Data a conferir
                                                    </span>
                                                </p>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <p className="text-gray-700 dark:text-slate-200">{MOTIVO_LABEL[s.motivo_categoria]}</p>
                                            {s.motivo_detalhe && <p className="text-[11px] text-gray-400 dark:text-slate-500 max-w-[260px] truncate" title={s.motivo_detalhe}>{s.motivo_detalhe}</p>}
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            {s.faturasEmAbertoQtd > 0 ? (
                                                <span className="text-red-600 dark:text-red-400 font-semibold">{s.faturasEmAbertoQtd} · {fmtBRL(s.faturasEmAbertoValor)}</span>
                                            ) : s.debitosAbertosValor ? (
                                                <span className="text-gray-500 dark:text-slate-400" title="Dívida informada na planilha de importação — não é fatura do sistema">{fmtBRL(s.debitosAbertosValor)} <span className="text-[10px] text-gray-400 dark:text-slate-500">informado</span></span>
                                            ) : <span className="text-gray-400 dark:text-slate-500">Nenhuma</span>}
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className={`text-[11px] font-bold px-2 py-1 rounded-md border ${STATUS_SOLICITACAO_CLS[s.status]}`}>{STATUS_SOLICITACAO_LABEL[s.status]}</span>
                                            {retornoMeta ? (
                                                <p className="mt-1.5">
                                                    <span title={`Retorno ao cliente previsto até ${fmtData(s.dataRetornoPrevista)}`}
                                                        className={`${SELO} ${retornoMeta.cls}`}>
                                                        {estadoRetorno === 'vencida' ? <AlertTriangle size={9} aria-hidden="true" /> : <Clock size={9} aria-hidden="true" />}
                                                        {estadoRetorno === 'vencida' ? `Retorno vencido há ${atraso} dia${atraso === 1 ? '' : 's'}` : `Retorno ${retornoMeta.label.toLowerCase()}`}
                                                    </span>
                                                </p>
                                            ) : null}
                                            {(() => {
                                                // #6127: resposta enviada e o cliente ainda não voltou.
                                                const r = estadoRetornoCliente(s, hojeBrasilia);
                                                if (r.estado !== 'aguardando') return null;
                                                return (
                                                    <p className="mt-1.5">
                                                        <span title="Sem retorno do cliente desde a resposta da Vigor — só avisa, o processo continua."
                                                            className={`${SELO} ${r.atrasado
                                                                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50'
                                                                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50'}`}>
                                                            <Clock size={9} aria-hidden="true" /> Aguardando cliente há {r.dias} dia{r.dias === 1 ? '' : 's'}
                                                        </span>
                                                    </p>
                                                );
                                            })()}
                                            {s.validadoEm ? (
                                                <p className="mt-1 text-[10px] font-bold text-primary inline-flex items-center gap-1">
                                                    <ShieldCheck size={9} /> Validado {fmtData(s.validadoEm)}
                                                </p>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <div className="flex flex-col items-start gap-1.5">
                                                <button onClick={e => { e.stopPropagation(); setSolicitacaoAbertaId(s.id); }}
                                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:opacity-80">
                                                    <Eye size={12} /> Abrir
                                                </button>
                                                <a href={linkGerenciarUc(s.uc_numero)} onClick={e => e.stopPropagation()}
                                                    title="Abre o cadastro desta UC — histórico completo do distrato e detalhes do rateio"
                                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200">
                                                    <ExternalLink size={12} /> Gerenciar UC
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {!loading && solicitacoesFiltradas.length > 0 ? (
                    /* md:pl-24: o botão flutuante da Vick fica no canto inferior esquerdo e
                        cobria o "1–50 de 169" (mesmo ajuste das listas do painel, #5840). */
                    <div className="flex items-center justify-between gap-3 px-4 md:pl-24 py-3 border-t border-gray-200 dark:border-slate-700 text-xs text-gray-500 dark:text-slate-400">
                        <span className="tabular-nums">
                            {paginaAtual.de}–{paginaAtual.ate} de {paginaAtual.total} solicitação(ões)
                        </span>
                        {paginaAtual.totalPaginas > 1 ? (
                            <div className="flex items-center gap-1" role="navigation" aria-label="Páginas da lista">
                                <button onClick={() => setPagina(p => p - 1)} disabled={paginaAtual.pagina <= 1} aria-label="Página anterior"
                                    className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
                                    <ChevronLeft size={14} />
                                </button>
                                <span className="px-2 tabular-nums">Página {paginaAtual.pagina} de {paginaAtual.totalPaginas}</span>
                                <button onClick={() => setPagina(p => p + 1)} disabled={paginaAtual.pagina >= paginaAtual.totalPaginas} aria-label="Próxima página"
                                    className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <NovaSolicitacaoCancelamentoModal isOpen={showNovaSolicitacao} onClose={() => setShowNovaSolicitacao(false)} onCreated={carregar} />

            <ImportarCancelamentosModal isOpen={showImportar} onClose={() => setShowImportar(false)} onImported={carregar} />

            <ConfirmModal
                isOpen={!!confirmDesfazer}
                onClose={() => { if (!confirmLoading) setConfirmDesfazer(null); }}
                onConfirm={handleDesfazerLote}
                loading={confirmLoading}
                variant="warning"
                title="Desfazer importação"
                message={confirmDesfazer
                    ? `Isso apaga os ${confirmDesfazer.quantidade} registro(s) que entraram por esta importação${confirmDesfazer.arquivo ? ` (${confirmDesfazer.arquivo})` : ''}. Solicitações criadas pela tela não são tocadas. Não há como recuperar depois — se precisar, basta importar a planilha de novo.`
                    : ''}
                confirmLabel="Desfazer importação"
            />

            <ConfirmModal
                isOpen={confirmLimparResolvidos}
                onClose={() => { if (!confirmLoading) setConfirmLimparResolvidos(false); }}
                onConfirm={handleLimparResolvidos}
                loading={confirmLoading}
                variant="info"
                title="Limpar alertas já resolvidos"
                message={`Marca como tratados os ${casosResolvidos.length} caso(s) cujo cliente já foi cancelado ou cuja solicitação de cancelamento já foi concluída (${casosResolvidos.reduce((t, c) => t + c.repeticoes, 0)} alertas contando as repetições do robô). Eles saem da tela; nada é apagado e o histórico do alerta continua no banco.`}
                confirmLabel="Limpar resolvidos"
            />

            <RespostaCancelamentoModal
                isOpen={!!respondendoSolicitacao}
                solicitacao={respondendoSolicitacao}
                onClose={() => setRespondendoSolicitacao(null)}
                onRegistrado={carregar}
            />

            <ValidarCancelamentoModal
                isOpen={!!validandoSolicitacao}
                solicitacao={validandoSolicitacao}
                onClose={() => { setValidandoSolicitacao(null); carregar(); }}
                onValidado={() => {
                    const s = validandoSolicitacao;
                    setValidandoSolicitacao(null);
                    // Emenda direto no distrato: validar e depois ter que achar a
                    // linha de novo na tabela seria um passo a mais sem motivo.
                    if (s) abrirDistrato(s);
                    carregar();
                }}
            />

            {distratoParaUc && (
                <CancelUCModal
                    isOpen={!!distratoParaUc}
                    onClose={() => { setDistratoParaUc(null); carregar(); }}
                    unidade={distratoParaUc.uc}
                    solicitacaoId={distratoParaUc.solicitacaoId}
                />
            )}

            <ConfirmModal
                isOpen={!!confirmAcao}
                onClose={() => { if (!confirmLoading) { setConfirmAcao(null); setMotivoConclusao(''); setCodigoRetencao(''); } }}
                onConfirm={handleConfirmarAcao}
                loading={confirmLoading}
                // Sem distrato ativo ao concluir é um aviso mais forte (não bloqueia — nem
                // todo motivo de cancelamento exige distrato formal, ex. mudança de endereço).
                variant={confirmAcao?.status === 'concluida' && confirmAcao.s.distratoStatus !== 'ativo' ? 'warning' : confirmAcao?.status === 'concluida' ? 'info' : 'warning'}
                title={confirmAcao?.status === 'concluida' ? 'Concluir solicitação de cancelamento' : 'Registrar desistência do cancelamento'}
                message={confirmAcao ? [
                    `Confirma marcar a solicitação de ${confirmAcao.s.cliente_nome} (UC ${confirmAcao.s.uc_numero}) como "${STATUS_SOLICITACAO_LABEL[confirmAcao.status]}"?`,
                    confirmAcao.status === 'concluida' && confirmAcao.s.distratoStatus !== 'ativo'
                        ? ' ⚠️ Nenhum distrato ativo foi gerado para esta solicitação — confirme que este cancelamento realmente não precisa de um (ex.: mudança de endereço) antes de concluir.'
                        : '',
                    ' Depois de confirmado, não há como reabrir esta solicitação pela tela — só ajustando diretamente no banco.',
                ].join('') : ''}
                confirmLabel={confirmAcao?.status === 'concluida' ? 'Concluir' : 'Confirmar desistência'}
                confirmDisabled={confirmAcao?.status === 'cancelada' && faltaNaRetencao(codigoRetencao, motivoConclusao) !== null}
            >
                {confirmAcao?.status === 'cancelada' && (
                    <div className="mb-3">
                        <label htmlFor="motivo-retencao" className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                            Por que o cliente ficou *
                        </label>
                        <select
                            id="motivo-retencao"
                            value={codigoRetencao}
                            onChange={e => setCodigoRetencao(e.target.value)}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                        >
                            <option value="">Escolha…</option>
                            {MOTIVOS_RETENCAO.map(m => <option key={m.codigo} value={m.codigo}>{m.rotulo}</option>)}
                        </select>
                    </div>
                )}
                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
                        {confirmAcao?.status === 'cancelada'
                            ? (motivoRetencaoPorCodigo(codigoRetencao)?.exigeDetalhe ? 'Detalhe *' : 'Detalhe (opcional)')
                            : 'Motivo (opcional)'}
                    </label>
                    <textarea
                        value={motivoConclusao}
                        onChange={e => setMotivoConclusao(e.target.value)}
                        rows={2}
                        placeholder={confirmAcao?.status === 'cancelada' ? 'O que foi oferecido ou resolvido…' : 'Observação sobre a conclusão (opcional)...'}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100 resize-y"
                    />
                </div>
            </ConfirmModal>

            {solicitacaoAberta && (
                <SolicitacaoCancelamentoModal
                    solicitacao={solicitacaoAberta}
                    envios={enviosPorSolicitacao.get(solicitacaoAberta.id)}
                    hojeBrasilia={hojeBrasilia}
                    bloqueado={modalDeAcaoAberto}
                    onClose={() => setSolicitacaoAbertaId(null)}
                    onValidar={() => setValidandoSolicitacao(solicitacaoAberta)}
                    onGerarDistrato={() => abrirDistrato(solicitacaoAberta)}
                    onResponder={() => setRespondendoSolicitacao(solicitacaoAberta)}
                    onHistorico={anchor => setHistoricoAberto({ solicitacaoId: solicitacaoAberta.id, anchor })}
                    onConcluir={() => { setMotivoConclusao(''); setCodigoRetencao(''); setConfirmAcao({ s: solicitacaoAberta, status: 'concluida' }); }}
                    onDesistir={() => { setMotivoConclusao(''); setCodigoRetencao(''); setConfirmAcao({ s: solicitacaoAberta, status: 'cancelada' }); }}
                    onCopiarProtocolo={copiarProtocolo}
                    onRegistrarRetorno={async (tipo, retornoEm, obs) => {
                        try {
                            await registrarRetornoClienteCancelamento(solicitacaoAberta.id, tipo, retornoEm, obs);
                            addToast('Retorno do cliente registrado.', 'success');
                            carregar();
                        } catch (e: any) {
                            addToast('Erro ao registrar o retorno: ' + (e.message || ''), 'error');
                            throw e;
                        }
                    }}
                />
            )}

            {historicoAberto && (
                <SolicitacaoHistoricoPopover
                    anchor={historicoAberto.anchor}
                    solicitacaoId={historicoAberto.solicitacaoId}
                    onClose={() => setHistoricoAberto(null)}
                />
            )}
        </div>
    );
};

export default CancelamentosView;
